/**
 * Choisir des sessions par centre, période et semaine, pour les listings et
 * les sous-groupes.
 *
 * L'écran s'ouvre sur la semaine qui vient : c'est elle qu'on imprime, le
 * vendredi ou le week-end avant le stage. Fouiller 69 sessions dans une liste
 * déroulante, comme avant, ne servait à personne.
 */

export interface SessionGroupe {
  id: string;
  centreId: string;
  centre: string;
  stage: string;
  periode: string;
  semaine: string;
  /** AAAA-MM-JJ */
  debut: string;
  fin: string;
  inscrits: number;
}

export interface Filtre {
  centreId: string;
  periode: string;
  semaine: string;
}

export const FILTRE_VIDE: Filtre = { centreId: '', periode: '', semaine: '' };

/**
 * La période et la semaine du prochain stage qui n'est pas terminé, ou du
 * dernier s'ils le sont tous.
 */
export function semaineParDefaut(
  sessions: SessionGroupe[],
  aujourdhui: string,
): { periode: string; semaine: string } | null {
  if (sessions.length === 0) return null;
  const parDebut = [...sessions].sort((a, b) => a.debut.localeCompare(b.debut));
  const prochaine = parDebut.find((s) => s.fin >= aujourdhui) ?? parDebut[parDebut.length - 1];
  return { periode: prochaine.periode, semaine: prochaine.semaine };
}

/** Les valeurs distinctes de `cle`, dans l'ordre chronologique de leur première session. */
function distinctesParDate(sessions: SessionGroupe[], cle: 'periode' | 'semaine'): string[] {
  const premiere = new Map<string, string>();
  for (const s of sessions) {
    const actuelle = premiere.get(s[cle]);
    if (actuelle === undefined || s.debut < actuelle) premiere.set(s[cle], s.debut);
  }
  return [...premiere.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([v]) => v);
}

export function periodesDisponibles(sessions: SessionGroupe[], centreId: string): string[] {
  return distinctesParDate(sessions.filter((s) => !centreId || s.centreId === centreId), 'periode');
}

/** Les semaines d'une période : « S1 » n'a de sens qu'avec sa période. */
export function semainesDisponibles(sessions: SessionGroupe[], centreId: string, periode: string): string[] {
  if (!periode) return [];
  return distinctesParDate(
    sessions.filter((s) => (!centreId || s.centreId === centreId) && s.periode === periode),
    'semaine',
  );
}

/** Les sessions du filtre, par centre puis par stage. */
export function filtrerSessions(sessions: SessionGroupe[], filtre: Filtre): SessionGroupe[] {
  return sessions
    .filter(
      (s) =>
        (!filtre.centreId || s.centreId === filtre.centreId) &&
        (!filtre.periode || s.periode === filtre.periode) &&
        (!filtre.semaine || s.semaine === filtre.semaine),
    )
    .sort(
      (a, b) =>
        a.centre.localeCompare(b.centre, 'fr') ||
        a.stage.localeCompare(b.stage, 'fr') ||
        a.debut.localeCompare(b.debut),
    );
}

/** Un nom de fichier lisible et sûr : « S1-Les-Colibris-presences.pdf ». */
export function nomDeFichier(...parties: string[]): string {
  return parties
    .join('-')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}
